import { ref, computed } from 'vue'

// 支持的语言类型
export type ErpLanguage = 'cn' | 'en' | 'de' | 'es' | 'fr' | 'it' | 'ja' | 'ru' | 'cn_tw'

// 语言配置
export const ERP_LANGUAGES = [
  { code: 'cn', name: '简体中文' },
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'it', name: 'Italiano' },
  { code: 'ja', name: '日本語' },
  { code: 'ru', name: 'Русский' },
  { code: 'cn_tw', name: '繁體中文' },
] as const

// 内置翻译数据
const translations: Record<ErpLanguage, Record<string, any>> = {
  cn: {
    designer: {
      templateName: '模板名称',
      paperType: '纸张类型',
      paperSettings: '纸张设置',
      baseControls: '基础控件',
      propertyPanel: '属性面板',
      selectElementToEdit: '选择一个元素来编辑属性',
      cancel: '取消',
      save: '保存',
      zoom: { zoomIn: '放大', zoomOut: '缩小' },
      actions: {
        undo: '撤销',
        redo: '恢复',
        rotate: '旋转',
        clear: '清空',
        confirmClear: '是否确认清空?',
        confirm: '确定',
        jsonPreview: 'JSON 预览',
        preview: '预览',
        print: '打印',
      },
      jsonView: {
        title: 'JSON',
        tidMode: 'tid模式',
        defaultMode: '默认',
        beautify: '美化',
        compress: '压缩',
        close: '关闭',
        noTemplate: '没有可用的模板',
        getJsonFailed: '获取JSON失败',
        showJsonFailed: '显示JSON视图失败',
      },
      templatePreview: {
        title: '打印预览',
        print: '打印',
        pdf: 'PDF',
        close: '关闭',
        templateEmpty: '模板对象不能为空',
        renderFailed: '渲染预览内容失败',
        showPreviewFailed: '显示预览失败',
        exportSuccess: '导出成功',
        exportFailed: '导出失败',
        exportFileName: '预览导出',
      },
      export: {
        exportPdfBlob: '导出pdf',
        exportPdfArrayBuffer: '导出获取pdf(ArrayBuffer)',
        exportPdfDataUrl: '导出获取pdf(DataUrl)',
        exportPdfBlobUrl: '导出获取pdf(BlobUrl)',
        exportPdfDataUrlString: '导出获取pdf(DataUrlString)',
        exportPdfObjectNewWindow: '导出并查看',
        defaultFileName: '导出文件',
      },
      print: {
        browser: '浏览器打印',
        printer: '打印机打印',
      },
      paperTypes: {
        selectPaperType: '请选择纸张类型',
        custom: '自定义',
      },
      messages: {
        selectTableFirst: '请先选中表格',
        printSuccess: '打印成功',
        printFailed: '打印失败',
        operationFailed: '操作失败',
      },
      upload: { hiddenButton: '隐藏上传按钮' },
      fonts: {
        microsoftYaHei: '微软雅黑',
        blackFont: '黑体',
        sourceHanSans: '思源黑体',
        wangXiZhi: '王羲之书法体',
        simsun: '宋体',
        stkaiti: '华为楷体',
      },
      test: { updatedElement: '这是更新后的元素' },
      controls: {
        text: '文本',
        longText: '长文本',
        image: '图片',
        emptyTable: '表格',
        rect: '矩形',
        hline: '横线',
        vline: '竖线',
        oval: '椭圆',
        html: 'html',
        barcode: '条码',
        qrcode: '二维码',
      },
    },
  },
  en: {
    designer: {
      templateName: 'Template Name',
      paperType: 'Paper Type',
      paperSettings: 'Paper Settings',
      baseControls: 'Base Controls',
      propertyPanel: 'Property Panel',
      selectElementToEdit: 'Select an element to edit properties',
      cancel: 'Cancel',
      save: 'Save',
      zoom: { zoomIn: 'Zoom In', zoomOut: 'Zoom Out' },
      actions: {
        undo: 'Undo',
        redo: 'Redo',
        rotate: 'Rotate',
        clear: 'Clear',
        confirmClear: 'Are you sure to clear?',
        confirm: 'Confirm',
        jsonPreview: 'JSON Preview',
        preview: 'Preview',
        print: 'Print',
      },
      jsonView: {
        title: 'JSON',
        tidMode: 'TID Mode',
        defaultMode: 'Default',
        beautify: 'Beautify',
        compress: 'Compress',
        close: 'Close',
        noTemplate: 'No template available',
        getJsonFailed: 'Failed to get JSON',
        showJsonFailed: 'Failed to show JSON view',
      },
      templatePreview: {
        title: 'Print Preview',
        print: 'Print',
        pdf: 'PDF',
        close: 'Close',
        templateEmpty: 'Template object cannot be empty',
        renderFailed: 'Failed to render preview content',
        showPreviewFailed: 'Failed to show preview',
        exportSuccess: 'Export successful',
        exportFailed: 'Export failed',
        exportFileName: 'Preview Export',
      },
      export: {
        exportPdfBlob: 'Export PDF',
        exportPdfArrayBuffer: 'Export PDF (ArrayBuffer)',
        exportPdfDataUrl: 'Export PDF (DataUrl)',
        exportPdfBlobUrl: 'Export PDF (BlobUrl)',
        exportPdfDataUrlString: 'Export PDF (DataUrlString)',
        exportPdfObjectNewWindow: 'Export and View',
        defaultFileName: 'Export File',
      },
      print: {
        browser: 'Browser Print',
        printer: 'Printer Print',
      },
      paperTypes: {
        selectPaperType: 'Please select paper type',
        custom: 'Custom',
      },
      messages: {
        selectTableFirst: 'Please select table first',
        printSuccess: 'Print Success',
        printFailed: 'Print Failed',
        operationFailed: 'Operation Failed',
      },
      upload: { hiddenButton: 'Hidden Upload Button' },
      fonts: {
        microsoftYaHei: 'Microsoft YaHei',
        blackFont: 'SimHei',
        sourceHanSans: 'Source Han Sans',
        wangXiZhi: 'WangXiZhi Calligraphy',
        simsun: 'SimSun',
        stkaiti: 'STKaiti',
      },
      test: { updatedElement: 'This is the updated element' },
      controls: {
        text: 'Text',
        longText: 'Long Text',
        image: 'Image',
        emptyTable: 'Table',
        rect: 'Rectangle',
        hline: 'Horizontal Line',
        vline: 'Vertical Line',
        oval: 'Oval',
        html: 'HTML',
        barcode: 'Barcode',
        qrcode: 'QR Code',
      },
    },
  },
  de: {
    designer: {
      templateName: 'Vorlagenname',
      paperType: 'Papiertyp',
      paperSettings: 'Papiereinstellungen',
      baseControls: 'Grundsteuerungen',
      propertyPanel: 'Eigenschaftenbereich',
      selectElementToEdit: 'Wählen Sie ein Element zum Bearbeiten der Eigenschaften',
      cancel: 'Abbrechen',
      save: 'Speichern',
      zoom: { zoomIn: 'Vergrößern', zoomOut: 'Verkleinern' },
      actions: {
        undo: 'Rückgängig',
        redo: 'Wiederholen',
        rotate: 'Drehen',
        clear: 'Löschen',
        confirmClear: 'Sind Sie sicher, dass Sie löschen möchten?',
        confirm: 'Bestätigen',
        jsonPreview: 'JSON-Vorschau',
        preview: 'Vorschau',
        print: 'Drucken',
      },
      jsonView: {
        title: 'JSON',
        tidMode: 'TID-Modus',
        defaultMode: 'Standard',
        beautify: 'Verschönern',
        compress: 'Komprimieren',
        close: 'Schließen',
        noTemplate: 'Keine Vorlage verfügbar',
        getJsonFailed: 'JSON abrufen fehlgeschlagen',
        showJsonFailed: 'JSON-Ansicht anzeigen fehlgeschlagen',
      },
      templatePreview: {
        title: 'Druckvorschau',
        print: 'Drucken',
        pdf: 'PDF',
        close: 'Schließen',
        templateEmpty: 'Vorlagenobjekt darf nicht leer sein',
        renderFailed: 'Vorschauinhalt rendern fehlgeschlagen',
        showPreviewFailed: 'Vorschau anzeigen fehlgeschlagen',
        exportSuccess: 'Export erfolgreich',
        exportFailed: 'Export fehlgeschlagen',
        exportFileName: 'Vorschau-Export',
      },
      export: {
        exportPdfBlob: 'PDF exportieren',
        exportPdfArrayBuffer: 'PDF exportieren (ArrayBuffer)',
        exportPdfDataUrl: 'PDF exportieren (DataUrl)',
        exportPdfBlobUrl: 'PDF exportieren (BlobUrl)',
        exportPdfDataUrlString: 'PDF exportieren (DataUrlString)',
        exportPdfObjectNewWindow: 'Exportieren und Anzeigen',
        defaultFileName: 'Export-Datei',
      },
      print: {
        browser: 'Im Browser drucken',
        printer: 'Mit Drucker drucken',
      },
      paperTypes: {
        selectPaperType: 'Bitte Papiertyp wählen',
        custom: 'Benutzerdefiniert',
      },
      messages: {
        selectTableFirst: 'Bitte zuerst Tabelle auswählen',
        printSuccess: 'Druck erfolgreich',
        printFailed: 'Druck fehlgeschlagen',
        operationFailed: 'Vorgang fehlgeschlagen',
      },
      upload: { hiddenButton: 'Versteckter Upload-Button' },
      fonts: {
        microsoftYaHei: 'Microsoft YaHei',
        blackFont: 'SimHei',
        sourceHanSans: 'Source Han Sans',
        wangXiZhi: 'WangXiZhi Kalligrafie',
        simsun: 'SimSun',
        stkaiti: 'STKaiti',
      },
      test: { updatedElement: 'Dies ist das aktualisierte Element' },
      controls: {
        text: 'Text',
        longText: 'Langer Text',
        image: 'Bild',
        emptyTable: 'Tabelle',
        rect: 'Rechteck',
        hline: 'Horizontale Linie',
        vline: 'Vertikale Linie',
        oval: 'Oval',
        html: 'HTML',
        barcode: 'Barcode',
        qrcode: 'QR-Code',
      },
    },
  },
  es: {
    designer: {
      templateName: 'Nombre de Plantilla',
      paperType: 'Tipo de Papel',
      paperSettings: 'Configuración de Papel',
      baseControls: 'Controles Básicos',
      propertyPanel: 'Panel de Propiedades',
      selectElementToEdit: 'Selecciona un elemento para editar propiedades',
      cancel: 'Cancelar',
      save: 'Guardar',
      zoom: { zoomIn: 'Acercar', zoomOut: 'Alejar' },
      actions: {
        undo: 'Deshacer',
        redo: 'Rehacer',
        rotate: 'Rotar',
        clear: 'Limpiar',
        confirmClear: '¿Estás seguro de limpiar?',
        confirm: 'Confirmar',
        jsonPreview: 'Vista previa JSON',
        preview: 'Vista previa',
        print: 'Imprimir',
      },
      jsonView: {
        title: 'JSON',
        tidMode: 'Modo TID',
        defaultMode: 'Predeterminado',
        beautify: 'Embellecer',
        compress: 'Comprimir',
        close: 'Cerrar',
        noTemplate: 'No hay plantilla disponible',
        getJsonFailed: 'Error al obtener JSON',
        showJsonFailed: 'Error al mostrar vista JSON',
      },
      templatePreview: {
        title: 'Vista Previa de Impresión',
        print: 'Imprimir',
        pdf: 'PDF',
        close: 'Cerrar',
        templateEmpty: 'El objeto de plantilla no puede estar vacío',
        renderFailed: 'Error al renderizar contenido de vista previa',
        showPreviewFailed: 'Error al mostrar vista previa',
        exportSuccess: 'Exportación exitosa',
        exportFailed: 'Error en exportación',
        exportFileName: 'Exportación de Vista Previa',
      },
      export: {
        exportPdfBlob: 'Exportar PDF',
        exportPdfArrayBuffer: 'Exportar PDF (ArrayBuffer)',
        exportPdfDataUrl: 'Exportar PDF (DataUrl)',
        exportPdfBlobUrl: 'Exportar PDF (BlobUrl)',
        exportPdfDataUrlString: 'Exportar PDF (DataUrlString)',
        exportPdfObjectNewWindow: 'Exportar y ver',
        defaultFileName: 'Archivo de Exportación',
      },
      print: {
        browser: 'Imprimir en navegador',
        printer: 'Imprimir en impresora',
      },
      paperTypes: {
        selectPaperType: 'Por favor selecciona el tipo de papel',
        custom: 'Personalizado',
      },
      messages: {
        selectTableFirst: 'Por favor selecciona la tabla primero',
        printSuccess: 'Impresión exitosa',
        printFailed: 'Impresión fallida',
        operationFailed: 'Operación fallida',
      },
      upload: { hiddenButton: 'Botón de Subida Oculto' },
      fonts: {
        microsoftYaHei: 'Microsoft YaHei',
        blackFont: 'SimHei',
        sourceHanSans: 'Source Han Sans',
        wangXiZhi: 'Caligrafía WangXiZhi',
        simsun: 'SimSun',
        stkaiti: 'STKaiti',
      },
      test: { updatedElement: 'Este es el elemento actualizado' },
      controls: {
        text: 'Texto',
        longText: 'Texto Largo',
        image: 'Imagen',
        emptyTable: 'Tabla',
        rect: 'Rectángulo',
        hline: 'Línea Horizontal',
        vline: 'Línea Vertical',
        oval: 'Óvalo',
        html: 'HTML',
        barcode: 'Código de Barras',
        qrcode: 'Código QR',
      },
    },
  },
  fr: {
    designer: {
      templateName: 'Nom du Modèle',
      paperType: 'Type de Papier',
      paperSettings: 'Paramètres de Papier',
      baseControls: 'Contrôles de Base',
      propertyPanel: 'Panneau de Propriétés',
      selectElementToEdit: 'Sélectionnez un élément pour modifier les propriétés',
      cancel: 'Annuler',
      save: 'Enregistrer',
      zoom: { zoomIn: 'Agrandir', zoomOut: 'Réduire' },
      actions: {
        undo: 'Annuler',
        redo: 'Refaire',
        rotate: 'Faire pivoter',
        clear: 'Effacer',
        confirmClear: 'Êtes-vous sûr de vouloir effacer?',
        confirm: 'Confirmer',
        jsonPreview: 'Aperçu JSON',
        preview: 'Aperçu',
        print: 'Imprimer',
      },
      jsonView: {
        title: 'JSON',
        tidMode: 'Mode TID',
        defaultMode: 'Par défaut',
        beautify: 'Embellir',
        compress: 'Compresser',
        close: 'Fermer',
        noTemplate: 'Aucun modèle disponible',
        getJsonFailed: 'Échec de récupération du JSON',
        showJsonFailed: "Échec d'affichage de la vue JSON",
      },
      templatePreview: {
        title: "Aperçu d'impression",
        print: 'Imprimer',
        pdf: 'PDF',
        close: 'Fermer',
        templateEmpty: "L'objet modèle ne peut pas être vide",
        renderFailed: "Échec du rendu du contenu d'aperçu",
        showPreviewFailed: "Échec d'affichage de l'aperçu",
        exportSuccess: 'Exportation réussie',
        exportFailed: "Échec de l'exportation",
        exportFileName: "Exportation d'aperçu",
      },
      export: {
        exportPdfBlob: 'Exporter le PDF',
        exportPdfArrayBuffer: 'Exporter PDF (ArrayBuffer)',
        exportPdfDataUrl: 'Exporter PDF (DataUrl)',
        exportPdfBlobUrl: 'Exporter PDF (BlobUrl)',
        exportPdfDataUrlString: 'Exporter PDF (DataUrlString)',
        exportPdfObjectNewWindow: 'Exporter et afficher',
        defaultFileName: "Fichier d'Exportation",
      },
      print: {
        browser: 'Imprimer avec le navigateur',
        printer: 'Imprimer avec l’imprimante',
      },
      paperTypes: {
        selectPaperType: 'Veuillez sélectionner le type de papier',
        custom: 'Personnalisé',
      },
      messages: {
        selectTableFirst: "Veuillez d'abord sélectionner le tableau",
        printSuccess: 'Impression réussie',
        printFailed: 'Impression échouée',
        operationFailed: 'Opération échouée',
      },
      upload: { hiddenButton: 'Bouton de Téléchargement Caché' },
      fonts: {
        microsoftYaHei: 'Microsoft YaHei',
        blackFont: 'SimHei',
        sourceHanSans: 'Source Han Sans',
        wangXiZhi: 'Calligraphie WangXiZhi',
        simsun: 'SimSun',
        stkaiti: 'STKaiti',
      },
      test: { updatedElement: "Ceci est l'élément mis à jour" },
      controls: {
        text: 'Texte',
        longText: 'Texte Long',
        image: 'Image',
        emptyTable: 'Tableau',
        rect: 'Rectangle',
        hline: 'Ligne Horizontale',
        vline: 'Ligne Verticale',
        oval: 'Ovale',
        html: 'HTML',
        barcode: 'Code-barres',
        qrcode: 'Code QR',
      },
    },
  },
  it: {
    designer: {
      templateName: 'Nome Modello',
      paperType: 'Tipo di Carta',
      paperSettings: 'Impostazioni Carta',
      baseControls: 'Controlli Base',
      propertyPanel: 'Pannello Proprietà',
      selectElementToEdit: 'Seleziona un elemento per modificare le proprietà',
      cancel: 'Annulla',
      save: 'Salva',
      zoom: { zoomIn: 'Ingrandisci', zoomOut: 'Rimpicciolisci' },
      actions: {
        undo: 'Annulla',
        redo: 'Ripeti',
        rotate: 'Ruota',
        clear: 'Cancella',
        confirmClear: 'Sei sicuro di voler cancellare?',
        confirm: 'Conferma',
        jsonPreview: 'Anteprima JSON',
        preview: 'Anteprima',
        print: 'Stampa',
      },
      jsonView: {
        title: 'JSON',
        tidMode: 'Modalità TID',
        defaultMode: 'Predefinito',
        beautify: 'Abbellire',
        compress: 'Comprimere',
        close: 'Chiudi',
        noTemplate: 'Nessun modello disponibile',
        getJsonFailed: 'Impossibile ottenere JSON',
        showJsonFailed: 'Impossibile mostrare la vista JSON',
      },
      templatePreview: {
        title: 'Anteprima di stampa',
        print: 'Stampa',
        pdf: 'PDF',
        close: 'Chiudi',
        templateEmpty: "L'oggetto modello non può essere vuoto",
        renderFailed: "Impossibile renderizzare il contenuto dell'anteprima",
        showPreviewFailed: "Impossibile mostrare l'anteprima",
        exportSuccess: 'Esportazione riuscita',
        exportFailed: 'Esportazione fallita',
        exportFileName: 'Esportazione anteprima',
      },
      export: {
        exportPdfBlob: 'Esporta PDF',
        exportPdfArrayBuffer: 'Esporta PDF (ArrayBuffer)',
        exportPdfDataUrl: 'Esporta PDF (DataUrl)',
        exportPdfBlobUrl: 'Esporta PDF (BlobUrl)',
        exportPdfDataUrlString: 'Esporta PDF (DataUrlString)',
        exportPdfObjectNewWindow: 'Esporta e visualizza',
        defaultFileName: 'File di Esportazione',
      },
      print: {
        browser: 'Stampa nel browser',
        printer: 'Stampa con stampante',
      },
      paperTypes: {
        selectPaperType: 'Si prega di selezionare il tipo di carta',
        custom: 'Personalizzato',
      },
      messages: {
        selectTableFirst: 'Si prega di selezionare prima la tabella',
        printSuccess: 'Stampa riuscita',
        printFailed: 'Stampa fallita',
        operationFailed: 'Operazione fallita',
      },
      upload: { hiddenButton: 'Pulsante Upload Nascosto' },
      fonts: {
        microsoftYaHei: 'Microsoft YaHei',
        blackFont: 'SimHei',
        sourceHanSans: 'Source Han Sans',
        wangXiZhi: 'Calligrafia WangXiZhi',
        simsun: 'SimSun',
        stkaiti: 'STKaiti',
      },
      test: { updatedElement: "Questo è l'elemento aggiornato" },
      controls: {
        text: 'Testo',
        longText: 'Testo Lungo',
        image: 'Immagine',
        emptyTable: 'Tabella',
        rect: 'Rettangolo',
        hline: 'Linea Orizzontale',
        vline: 'Linea Verticale',
        oval: 'Ovale',
        html: 'HTML',
        barcode: 'Codice a Barre',
        qrcode: 'Codice QR',
      },
    },
  },
  ja: {
    designer: {
      templateName: 'テンプレート名',
      paperType: '用紙タイプ',
      paperSettings: '用紙設定',
      baseControls: '基本コントロール',
      propertyPanel: 'プロパティパネル',
      selectElementToEdit: 'プロパティを編集する要素を選択してください',
      cancel: 'キャンセル',
      save: '保存',
      zoom: { zoomIn: '拡大', zoomOut: '縮小' },
      actions: {
        undo: '元に戻す',
        redo: 'やり直し',
        rotate: '回転',
        clear: 'クリア',
        confirmClear: 'クリアしてもよろしいですか？',
        confirm: '確認',
        jsonPreview: 'JSONプレビュー',
        preview: 'プレビュー',
        print: '印刷',
      },
      jsonView: {
        title: 'JSON',
        tidMode: 'TIDモード',
        defaultMode: 'デフォルト',
        beautify: '整形',
        compress: '圧縮',
        close: '閉じる',
        noTemplate: '利用可能なテンプレートがありません',
        getJsonFailed: 'JSON取得に失敗しました',
        showJsonFailed: 'JSONビューの表示に失敗しました',
      },
      templatePreview: {
        title: '印刷プレビュー',
        print: '印刷',
        pdf: 'PDF',
        close: '閉じる',
        templateEmpty: 'テンプレートオブジェクトは空にできません',
        renderFailed: 'プレビューコンテンツのレンダリングに失敗しました',
        showPreviewFailed: 'プレビューの表示に失敗しました',
        exportSuccess: 'エクスポート成功',
        exportFailed: 'エクスポート失敗',
        exportFileName: 'プレビューエクスポート',
      },
      export: {
        exportPdfBlob: 'PDF をエクスポート',
        exportPdfArrayBuffer: 'PDF エクスポート (ArrayBuffer)',
        exportPdfDataUrl: 'PDF エクスポート (DataUrl)',
        exportPdfBlobUrl: 'PDF エクスポート (BlobUrl)',
        exportPdfDataUrlString: 'PDF エクスポート (DataUrlString)',
        exportPdfObjectNewWindow: 'エクスポートして表示',
        defaultFileName: 'エクスポートファイル',
      },
      print: {
        browser: 'ブラウザーで印刷',
        printer: 'プリンターで印刷',
      },
      paperTypes: {
        selectPaperType: '用紙タイプを選択してください',
        custom: 'カスタム',
      },
      messages: {
        selectTableFirst: '最初にテーブルを選択してください',
        printSuccess: '印刷成功',
        printFailed: '印刷失敗',
        operationFailed: '操作失敗',
      },
      upload: { hiddenButton: '非表示アップロードボタン' },
      fonts: {
        microsoftYaHei: 'Microsoft YaHei',
        blackFont: 'SimHei',
        sourceHanSans: 'Source Han Sans',
        wangXiZhi: '王羲之書体',
        simsun: 'SimSun',
        stkaiti: 'STKaiti',
      },
      test: { updatedElement: 'これは更新された要素です' },
      controls: {
        text: 'テキスト',
        longText: '長いテキスト',
        image: '画像',
        emptyTable: 'テーブル',
        rect: '矩形',
        hline: '水平線',
        vline: '垂直線',
        oval: '楕円',
        html: 'HTML',
        barcode: 'バーコード',
        qrcode: 'QRコード',
      },
    },
  },
  ru: {
    designer: {
      templateName: 'Имя Шаблона',
      paperType: 'Тип Бумаги',
      paperSettings: 'Настройки Бумаги',
      baseControls: 'Базовые Элементы',
      propertyPanel: 'Панель Свойств',
      selectElementToEdit: 'Выберите элемент для редактирования свойств',
      cancel: 'Отмена',
      save: 'Сохранить',
      zoom: { zoomIn: 'Увеличить', zoomOut: 'Уменьшить' },
      actions: {
        undo: 'Отменить',
        redo: 'Повторить',
        rotate: 'Повернуть',
        clear: 'Очистить',
        confirmClear: 'Вы уверены, что хотите очистить?',
        confirm: 'Подтвердить',
        jsonPreview: 'Предварительный просмотр JSON',
        preview: 'Предварительный просмотр',
        print: 'Печать',
      },
      jsonView: {
        title: 'JSON',
        tidMode: 'Режим TID',
        defaultMode: 'По умолчанию',
        beautify: 'Украсить',
        compress: 'Сжать',
        close: 'Закрыть',
        noTemplate: 'Шаблон недоступен',
        getJsonFailed: 'Не удалось получить JSON',
        showJsonFailed: 'Не удалось показать JSON-вид',
      },
      templatePreview: {
        title: 'Предварительный просмотр печати',
        print: 'Печать',
        pdf: 'PDF',
        close: 'Закрыть',
        templateEmpty: 'Объект шаблона не может быть пустым',
        renderFailed: 'Не удалось отрендерить содержимое предварительного просмотра',
        showPreviewFailed: 'Не удалось показать предварительный просмотр',
        exportSuccess: 'Экспорт успешен',
        exportFailed: 'Экспорт не удался',
        exportFileName: 'Экспорт предварительного просмотра',
      },
      export: {
        exportPdfBlob: 'Экспорт PDF',
        exportPdfArrayBuffer: 'Экспорт PDF (ArrayBuffer)',
        exportPdfDataUrl: 'Экспорт PDF (DataUrl)',
        exportPdfBlobUrl: 'Экспорт PDF (BlobUrl)',
        exportPdfDataUrlString: 'Экспорт PDF (DataUrlString)',
        exportPdfObjectNewWindow: 'Экспорт и просмотр',
        defaultFileName: 'Файл Экспорта',
      },
      print: {
        browser: 'Печать в браузере',
        printer: 'Печать на принтере',
      },
      paperTypes: {
        selectPaperType: 'Пожалуйста, выберите тип бумаги',
        custom: 'Пользовательский',
      },
      messages: {
        selectTableFirst: 'Пожалуйста, сначала выберите таблицу',
        printSuccess: 'Печать успешна',
        printFailed: 'Печать не удалась',
        operationFailed: 'Операция не удалась',
      },
      upload: { hiddenButton: 'Скрытая Кнопка Загрузки' },
      fonts: {
        microsoftYaHei: 'Microsoft YaHei',
        blackFont: 'SimHei',
        sourceHanSans: 'Source Han Sans',
        wangXiZhi: 'Каллиграфия WangXiZhi',
        simsun: 'SimSun',
        stkaiti: 'STKaiti',
      },
      test: { updatedElement: 'Это обновленный элемент' },
      controls: {
        text: 'Текст',
        longText: 'Длинный Текст',
        image: 'Изображение',
        emptyTable: 'Таблица',
        rect: 'Прямоугольник',
        hline: 'Горизонтальная Линия',
        vline: 'Вертикальная Линия',
        oval: 'Овал',
        html: 'HTML',
        barcode: 'Штрих-код',
        qrcode: 'QR-код',
      },
    },
  },
  cn_tw: {
    designer: {
      templateName: '範本名稱',
      paperType: '紙張類型',
      paperSettings: '紙張設定',
      baseControls: '基礎控件',
      propertyPanel: '屬性面板',
      selectElementToEdit: '選擇一個元素來編輯屬性',
      cancel: '取消',
      save: '儲存',
      zoom: { zoomIn: '放大', zoomOut: '縮小' },
      actions: {
        undo: '復原',
        redo: '重做',
        rotate: '旋轉',
        clear: '清空',
        confirmClear: '是否確認清空?',
        confirm: '確定',
        jsonPreview: 'JSON 預覽',
        preview: '預覽',
        print: '列印',
      },
      jsonView: {
        title: 'JSON',
        tidMode: 'tid模式',
        defaultMode: '默认',
        beautify: '美化',
        compress: '压缩',
        close: '关闭',
        noTemplate: '没有可用的模板',
        getJsonFailed: '获取JSON失败',
        showJsonFailed: '显示JSON视图失败',
      },
      templatePreview: {
        title: '打印预览',
        print: '打印',
        pdf: 'PDF',
        close: '关闭',
        templateEmpty: '模板对象不能为空',
        renderFailed: '渲染预览内容失败',
        showPreviewFailed: '显示预览失败',
        exportSuccess: '导出成功',
        exportFailed: '导出失败',
        exportFileName: '预览导出',
      },
      export: {
        exportPdfBlob: '匯出PDF',
        exportPdfArrayBuffer: '匯出取得pdf(ArrayBuffer)',
        exportPdfDataUrl: '匯出取得pdf(DataUrl)',
        exportPdfBlobUrl: '匯出取得pdf(BlobUrl)',
        exportPdfDataUrlString: '匯出取得pdf(DataUrlString)',
        exportPdfObjectNewWindow: '匯出並檢視',
        defaultFileName: '匯出檔案',
      },
      print: {
        browser: '瀏覽器列印',
        printer: '印表機列印',
      },
      paperTypes: {
        selectPaperType: '請選擇紙張類型',
        custom: '自定義',
      },
      messages: {
        selectTableFirst: '請先選中表格',
        printSuccess: '列印成功',
        printFailed: '列印失敗',
        operationFailed: '操作失敗',
      },
      upload: { hiddenButton: '隱藏上傳按鈕' },
      fonts: {
        microsoftYaHei: '微軟雅黑',
        blackFont: '黑體',
        sourceHanSans: '思源黑體',
        wangXiZhi: '王羲之書法體',
        simsun: '宋體',
        stkaiti: '華為楷體',
      },
      test: { updatedElement: '這是更新後的元素' },
      controls: {
        text: '文字',
        longText: '長文字',
        image: '圖片',
        emptyTable: '表格',
        rect: '矩形',
        hline: '橫線',
        vline: '豎線',
        oval: '橢圓',
        html: 'html',
        barcode: '條碼',
        qrcode: '二維碼',
      },
    },
  },
}

// 全局语言状态
const currentLanguage = ref<ErpLanguage>('cn')

// 获取浏览器语言
function getBrowserLanguage(): ErpLanguage {
  const browserLang = navigator.language.toLowerCase()

  // 精确匹配
  for (const lang of ERP_LANGUAGES) {
    if (browserLang === lang.code || browserLang === lang.code.replace('_', '-')) {
      return lang.code
    }
  }

  // 模糊匹配（只匹配主语言代码）
  const primaryLang = browserLang.split('-')[0]
  for (const lang of ERP_LANGUAGES) {
    if (lang.code.split('_')[0] === primaryLang) {
      return lang.code
    }
  }

  return 'cn' // 默认中文
}

// 获取翻译值
function getTranslation(key: string, lang: ErpLanguage = currentLanguage.value): string {
  const keys = key.split('.')
  let value: any = translations[lang]

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k]
    } else {
      console.warn(`[ERP-I18N] Translation missing for key: ${key} in language: ${lang}`)
      return keys[keys.length - 1] || key
    }
  }

  return typeof value === 'string' ? value : key
}

// 主要的hook函数
export function useErpI18n() {
  // 翻译函数
  const t = (key: string): string => {
    return getTranslation(key, currentLanguage.value)
  }

  // 当前语言信息
  const currentLang = computed(() => currentLanguage.value)

  // 当前语言显示名称
  const currentLanguageName = computed(() => {
    const lang = ERP_LANGUAGES.find((l) => l.code === currentLanguage.value)
    return lang?.name || '简体中文'
  })

  // 支持的语言列表
  const supportedLanguages = computed(() => ERP_LANGUAGES)

  // 切换语言
  const switchLanguage = (lang: ErpLanguage) => {
    if (ERP_LANGUAGES.some((l) => l.code === lang)) {
      currentLanguage.value = lang
      localStorage.setItem('erp-print-language', lang)

      // 同步hiprint语言
      syncHiprintLanguage(lang)

      console.log(`[ERP-I18N] Language switched to: ${lang}`)
      return lang
    }
    console.warn(`[ERP-I18N] Unsupported language: ${lang}`)
    return currentLanguage.value
  }

  // 同步hiprint语言
  const syncHiprintLanguage = (lang: ErpLanguage) => {
    if (window.hiprint?.i18n) {
      window.hiprint.i18n.lang = lang
      console.log(`[ERP-I18N] Synchronized hiprint language to: ${lang}`)
    }
  }

  // 初始化语言
  const initLanguage = () => {
    // 优先级：localStorage > 浏览器语言 > 默认中文
    const savedLang = localStorage.getItem('erp-print-language') as ErpLanguage
    if (savedLang && ERP_LANGUAGES.some((l) => l.code === savedLang)) {
      currentLanguage.value = savedLang
    } else {
      currentLanguage.value = getBrowserLanguage()
    }

    // 初始化时同步hiprint语言
    setTimeout(() => syncHiprintLanguage(currentLanguage.value), 100)
  }

  // 获取hiprint翻译（兼容性）
  const getHiprintTranslation = (): any => {
    try {
      // 这里可以从hiprint的i18n文件夹获取翻译
      // 暂时返回空对象，如果需要可以扩展
      return {}
    } catch {
      return {}
    }
  }

  // 初始化
  initLanguage()

  return {
    t,
    currentLanguage: currentLang,
    currentLanguageName,
    supportedLanguages,
    switchLanguage,
    syncHiprintLanguage,
    initLanguage,
    getHiprintTranslation,
    // 兼容性属性
    locale: computed(() => currentLanguage.value),
  }
}

// 类型声明
declare global {
  interface Window {
    hiprint?: any & {
      i18n?: {
        lang: string
        languages?: Record<string, any>
      }
    }
  }
}

export default useErpI18n
